import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AnswerKeyIcon, ChartBarIcon, ClipboardListIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import { useQuizStore } from './store';
import '../../styles/rail-drag.css';

/** Kết quả driven from outside the quiz store, e.g. an IZ test unit's real class results card. */
export interface QuizResultsToggle {
  open: boolean;
  onToggle: () => void;
}

export interface QuizControlsProps {
  /** The open quiz document id - reopening a quiz starts with the answers hidden again. */
  documentId: string;
  /** Đáp án; off when no answer keys loaded, e.g. a matrix exam. */
  answers?: boolean;
  /** Kết quả lớp; the generated quiz's own panel when omitted. */
  results?: QuizResultsToggle;
  /** Chi tiết lớp, the whole-class list; only real class results have it. */
  detail?: QuizResultsToggle;
}

// The right-hand mirror of the left rail: 24px glyph over a stable noun, one 85px card.
// It floats in the free canvas margin beside the quiz artboard and never over it, because DOM above
// the board canvas swallows pen strokes - revealing is a repaint, not an overlay. See MOCKS.md.
export function QuizControls({ documentId, answers = true, results, detail }: QuizControlsProps) {
  const { t } = useTranslation('quiz');
  const revealed = useQuizStore((state) => state.revealed);
  const quizResultsOpen = useQuizStore((state) => state.resultsOpen);
  const toggleReveal = useQuizStore((state) => state.toggleReveal);
  const toggleQuizResults = useQuizStore((state) => state.toggleResults);
  const resultsOpen = results ? results.open : quizResultsOpen;
  const toggleResults = results ? results.onToggle : toggleQuizResults;
  const reset = useQuizStore((state) => state.reset);

  // docked only while the report is DOCKED: that is the one state reserving the cluster's lane in
  // --board-inset-right, so the card must sit exactly there. Otherwise the spot is the teacher's.
  const resultsDocked = useQuizStore((state) => state.resultsDocked);
  const docked = !results && quizResultsOpen && resultsDocked;
  const drag = useDraggableChrome({ id: 'quiz-rail', enabled: !docked });

  // least surprise: the quiz always opens unrevealed, whatever the last document left behind
  useEffect(() => { reset(); }, [documentId, reset]);

  const className = [
    docked ? 'quiz-rail quiz-rail--docked' : 'quiz-rail quiz-rail--floating',
    drag.dragging ? 'quiz-rail--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={drag.ref} className={className} role="group" aria-label={t('controls.group')}>
      {/* hidden by quiz.css when docked */}
      <button
        type="button"
        className="quiz-rail__handle"
        aria-label={t('controls.drag')}
        {...drag.handleProps}
      >
        <MoveIcon size={18} />
      </button>
      {/* the visible label is a fixed noun, so the accessible name carries the action and state */}
      {answers ? <button
        type="button"
        className={revealed ? 'quiz-rail__item quiz-rail__item--active' : 'quiz-rail__item'}
        aria-pressed={revealed}
        aria-label={revealed ? t('controls.revealOff') : t('controls.revealOn')}
        onClick={toggleReveal}
      >
        <span className="quiz-rail__icon" aria-hidden={true}><AnswerKeyIcon size={24} /></span>
        <span className="quiz-rail__label">{t('controls.answers')}</span>
      </button> : null}
      {/* opens the results panel beside the canvas; the board re-fits, nothing covers it */}
      <button
        type="button"
        className={resultsOpen ? 'quiz-rail__item quiz-rail__item--active' : 'quiz-rail__item'}
        aria-pressed={resultsOpen}
        aria-label={resultsOpen ? t('controls.resultsOff') : t('controls.resultsOn')}
        onClick={toggleResults}
      >
        <span className="quiz-rail__icon" aria-hidden={true}><ChartBarIcon size={24} /></span>
        <span className="quiz-rail__label">{t('controls.results')}</span>
      </button>
      {detail ? <button
        type="button"
        className={detail.open ? 'quiz-rail__item quiz-rail__item--active' : 'quiz-rail__item'}
        aria-pressed={detail.open}
        aria-label={detail.open ? t('controls.detailOff') : t('controls.detailOn')}
        onClick={detail.onToggle}
      >
        <span className="quiz-rail__icon" aria-hidden={true}><ClipboardListIcon size={24} /></span>
        <span className="quiz-rail__label">{t('controls.detail')}</span>
      </button> : null}
    </div>
  );
}
